import { NavigationBack, Typography } from "@/components";
import FloatingSearchV2 from "@/components/ui/floating-search/v2";
import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

const routeContacts = [
	"/dashboard/contacts/contact",
	"/dashboard/contacts/contact/teams",
] as const;

const routeDirections = [
	"/dashboard/contacts/directory",
	"/dashboard/contacts/directory/teams",
] as const;

interface AppBarContactDirectoryProps {
	activeTab?: 0 | 1;
}

const AppBarContactDirectory = (props: AppBarContactDirectoryProps) => {
	const { pathname, isContact } = useLocation({
		select: (location) => ({
			pathname: location.pathname,
			isContact: location.pathname.includes("/contacts/contact"),
		}),
	});
	const navigate = useNavigate();
	const routeTabs = useMemo(
		() => (isContact ? routeContacts : routeDirections),
		[isContact],
	);
	const searchQ = useSearch({
		strict: false,
		select: (search) => search.q ?? "",
	});
	const handleChange = (e: React.SyntheticEvent, newValue: number) => {
		e?.stopPropagation();
		e?.preventDefault();
		const selectedTab = routeTabs[newValue];
		return navigate({
			replace: true,
			to: selectedTab,
			resetScroll: true,
		});
	};

	const activeTab = useMemo(() => {
		if (props.activeTab) return props.activeTab;
		const activeIdx = routeTabs.findIndex(
			(routeContact) => routeContact === pathname,
		);
		return activeIdx >= 0 ? activeIdx : 0;
	}, [routeTabs, pathname, props.activeTab]);

	const onChangeSearch = useCallback(
		(value: string) =>
			navigate({
				search: {
					// @ts-expect-error
					q: value || undefined,
				},
				replace: true,
			}),
		[navigate],
	);

	return (
		<AppBar
			position="relative"
			color="transparent"
			className="!bg-gradient-contact [&_*_]:text-white"
		>
			<Toolbar>
				<NavigationBack />
				<div className="w-full relative flex gap-1 justify-between py-3">
					<FloatingSearchV2
						name="search"
						value={searchQ}
						onClear={() => onChangeSearch("")}
						onChange={(e) => onChangeSearch(e.target.value)}
						className="!rounded-xl"
						inputClassName="!rounded-xl"
					>
						<div className="flex items-center">
							<Typography fontWeight={700} fontSize={16}>
								{isContact ? "Contacts" : "Directory"}
							</Typography>
						</div>
					</FloatingSearchV2>
				</div>
			</Toolbar>
			<Toolbar classes={{ root: "!p-0 !items-end" }}>
				<Tabs
					value={activeTab}
					onChange={handleChange}
					indicatorColor="primary"
					textColor="inherit"
					variant="fullWidth"
					aria-label="full width tabs example"
					className="w-full [&_span.MuiTabs-indicator]:!bg-white"
				>
					<Tab label="People" sx={{ textTransform: "none" }} />
					<Tab label="Teams" sx={{ textTransform: "none" }} />
				</Tabs>
			</Toolbar>
		</AppBar>
	);
};

export default AppBarContactDirectory;
